import type { ResearchProvenance } from '@testfeed/integration-contracts'
import { insightOpportunityCopy as copy } from '@testfeed/product-language'

/** Server-permitted historical evidence, kept separate from measured store facts and generated copy. */
export function InsightOpportunityResearch({ research }: { research: ResearchProvenance | undefined }) {
  if (!research || research.status === 'not_requested')
    return <s-text color="subdued">{copy.researchNotRequested}</s-text>
  if (research.status === 'restricted') return <s-banner tone="warning">{copy.researchRestricted}</s-banner>
  const { selection, evidenceUses } = research
  if (selection.status === 'no_match') return <s-text color="subdued">{copy.researchNoMatch}</s-text>
  if (selection.status !== 'selected') return <s-banner tone="warning">{copy.researchRestricted}</s-banner>
  let unused = null
  if (!evidenceUses.length) unused = <s-text color="subdued">{copy.researchUnused}</s-text>
  let newer = null
  if (research.newerCaseIds?.length) newer = <s-paragraph>{copy.researchNewer}</s-paragraph>
  return (
    <s-stack gap="base">
      <s-text fontWeight="semibold">{copy.researchTitle}</s-text>
      <s-paragraph>{copy.researchCaveat}</s-paragraph>
      {newer}
      {unused}
      {evidenceUses.map((use, index) => {
        const target = use.alternativeId ? copy.researchAlternative(use.alternativeId) : copy.researchField
        const cited = selection.cases
          .flatMap(({ passages }) => passages)
          .filter(({ passage }) => use.passageRefs.includes(passage.passageId))
        return (
          <s-stack key={index} gap="small-100">
            <s-text fontWeight="medium">{copy.researchUse(target, copy.researchRelations[use.relation])}</s-text>
            <s-paragraph>{use.rationale}</s-paragraph>
            <s-paragraph>{use.applicabilityLimit}</s-paragraph>
            {cited.map(({ passage, source }) => (
              <s-link key={passage.passageId} href={source.url} target="_blank">
                {source.title}
              </s-link>
            ))}
          </s-stack>
        )
      })}
      {selection.cases.map(({ revision, passages }) => (
        <s-stack key={revision.revisionId} gap="small-100">
          <s-text fontWeight="medium">{revision.title}</s-text>
          <s-text>{copy.researchInterpretation}</s-text>
          <s-paragraph>{revision.interpretation}</s-paragraph>
          <s-text>{copy.researchLimitations}</s-text>
          <s-paragraph>{revision.limitations}</s-paragraph>
          <s-paragraph>{revision.applicability}</s-paragraph>
          {passages.map(({ passage, source }) => (
            <s-stack key={passage.passageId} gap="small-100">
              <s-link href={source.url} target="_blank">
                {source.title}
              </s-link>
              <s-text color="subdued">{source.publisher}</s-text>
              <s-text>{copy.researchQuote}</s-text>
              <s-paragraph>{passage.quote}</s-paragraph>
              <s-text color="subdued">{source.completeness}</s-text>
            </s-stack>
          ))}
        </s-stack>
      ))}
    </s-stack>
  )
}
